/*
    The link carries its token in the URL fragment (`/public/confirm#t=<token>`), so it never reaches
    a server log or a Referer. The page keeps it in memory and drops it from the address bar; the copy
    in sessionStorage only lets an accidental reload (pull to refresh) continue in the same tab, and is
    removed as soon as the link can't be used any more. Never log the token.
*/

const STORAGE_KEY = 'crqlar.digitalSigning.token';

/** The URL-decoded `t` parameter of a fragment like `#t=abc&x=1`; undefined when missing or empty. */
export const tokenFromHash = (hash: string | undefined): string | undefined => {
    if (!hash) return undefined;

    const token = new URLSearchParams(hash.replace(/^#/, '')).get('t')?.trim();
    return token || undefined;
};

// Storage can throw (private mode, blocked site data); the page then just can't survive a reload.

export const rememberedToken = (): string | undefined => {
    try {
        return window.sessionStorage.getItem(STORAGE_KEY) || undefined;
    } catch {
        return undefined;
    }
};

export const rememberToken = (token: string) => {
    try {
        window.sessionStorage.setItem(STORAGE_KEY, token);
    } catch {
    }
};

export const forgetToken = () => {
    try {
        window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
    }
};

/** The fragment's token, else the one remembered from an earlier load in this tab. */
export const currentToken = (): string | undefined => tokenFromHash(window.location.hash) ?? rememberedToken();

/**
 * Remembers [token] for a reload and replaces the history entry with the fragment-less URL, keeping
 * the router's history state.
 */
export const keepTokenOutOfUrl = (token: string) => {
    rememberToken(token);
    if (!window.location.hash) return;

    try {
        window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
    } catch {
    }
};
